import { Pagination } from "@/models/query-params.model";
import { ITheme, IThemeColors, Theme } from "@/models/theme.model";
import { api } from "@/services/api-config/setup";
import { getFileUrl } from "@/utils/generics.util";
import axios from "axios";
import Color from "color";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useAuthStore } from "./auth";
import { instance } from "./index";

export interface ThemeImage {
  url?: string;
  type?: string;
  image?: File;
}

export interface ThemeResponse {
  id?: string;
  theme: ITheme | null;
  images: ThemeImage[];
  subdomain?: string;
}

const defaultTheme = {
  colors: {
    "h-primary": "#153c59",
    "h-secondary": "#d2b604",
  },
};

export function generateShades(hex: string) {
  const baseColor = Color(hex);
  return {
    50: baseColor.lightness(95).hex(),
    100: baseColor.lightness(90).hex(),
    200: baseColor.lightness(80).hex(),
    300: baseColor.lightness(70).hex(),
    400: baseColor.lightness(65).hex(),
    500: baseColor.hex(),
    600: baseColor.mix(Color("black"), 0.3).hex(),
    700: baseColor.mix(Color("black"), 0.4).hex(),
    800: baseColor.mix(Color("black"), 0.5).hex(),
    900: baseColor.mix(Color("black"), 0.6).hex(),
  };
}

const store = defineStore({
  id: "theme",
  state: () => ({
    theme: ref<ITheme | null>(null),
    logo: ref<any>(null),
    partners: ref<any[] | null>(null),
    files: ref<ThemeResponse>({ theme: null, images: [] }),
    filesById: ref<ThemeResponse>({ theme: null, images: [] }),
    list: ref<Pagination<Theme> | null>(null),
    themeError: ref(false),
    themeId: ref(""),
    subdomain: ref(""),
  }),
  actions: {
    getTheme() {
      api("/white-label")
        .get(`/theme.json`)
        .then((response) => {
          this.theme = response.data;
          this.applyTheme(response.data);
          this.themeError = false;
        })
        .catch(() => {
          this.applyTheme();
          this.themeError = true;
        });
    },

    async getLogo() {
      await api("/white-label")
        .get(`/images/logo.svg`, { responseType: "blob" })
        .then((response) => (this.logo = getFileUrl(response.data)))
        .catch(async () => await this.getDefaultLogo());
    },

    async getPartners() {
      this.partners = [];
      await api("/partners-only")
        .get(`/images/partners`)
        .then(async (response) => {
          if (response.data.length !== 0) {
            await Promise.all([
              ...response.data.map((e) =>
                this.getImagePartners(`/images/partners/${e}`)
              ),
            ]);
          } else await this.getDefaultPartners();
        })
        .catch(async () => await this.getDefaultPartners());
    },

    async getFiles() {
      if (useAuthStore.data)
        await api()
          .get("/themes/files")
          .then(async (response) => {
            const { images, theme, id, subdomain } = response.data;
            this.themeId = id;
            this.files.theme = theme;
            this.files.images = [];
            this.files.subdomain = subdomain;

            await Promise.all([
              this.getThemeJson(theme),
              ...images?.map((e) => this.getImage(e?.url, e?.type)),
            ]);
          });
    },

    async getThemeJson(themeUrl: string) {
      if (themeUrl)
        await api("/no-cache")
          .get(themeUrl)
          .then((response) => (this.files.theme = response.data));
      else this.files.theme = defaultTheme;
    },

    async getImage(url: string, type?: string) {
      await api("/no-cache")
        .get(url, { responseType: "blob" })
        .then((response) => {
          const file = new File([response.data], `${url.split("/").pop()}`, {
            type: "image/svg+xml",
          });
          this.files.images.push({ type, image: file });
        });
    },

    async getImagePartners(url: string) {
      await api("/white-label")
        .get(url, { responseType: "blob" })
        .then((response) => {
          const file = new File([response.data], `${url.split("/").pop()}`, {
            type: "image/svg+xml",
          });
          this.partners?.push(file);
        });
    },

    async getDefaultLogo() {
      const image = await axios(`/images/logo.svg`, { responseType: "blob" });
      this.logo = getFileUrl(image.data);
    },

    async getDefaultPartners() {
      const context = require.context(
        "../assets/images/partners",
        false,
        /\.(png|jpe?g|svg)$/
      );

      this.partners = context.keys().map(context);
    },

    async getFilesById(id: string) {
      if (useAuthStore.data) {
        await api()
          .get(`/themes/files`, {
            params: { organizationId: id },
          })
          .then(async (response) => {
            const { images, theme, id, subdomain } = response.data;
            this.themeId = id;
            this.filesById.theme = theme;
            this.filesById.images = [];
            this.filesById.subdomain = subdomain;
            if (images)
              await Promise.all([
                this.getThemeJsonById(theme, subdomain),
                ...images.map((e) =>
                  this.getImageById(e.url, e.type, subdomain)
                ),
              ]);
          })
          .catch(() => {
            this.filesById.theme = defaultTheme;
          });
      }
    },

    async getThemeJsonById(themeUrl: string, domain) {
      if (themeUrl)
        await api("/no-cache")
          .get(themeUrl, { headers: { "x-domain": domain } })
          .then((response) => {
            this.filesById.theme = response.data;
          });
      else this.filesById.theme = defaultTheme;
    },

    async getImageById(url: string, type?: string, domain?: string) {
      await api("/no-cache")
        .get(url, { responseType: "blob", headers: { "x-domain": domain } })
        .then((response) => {
          const file = new File([response.data], `${url.split("/").pop()}`, {
            type: "image/svg+xml",
          });
          this.filesById.images.push({ type, image: file });
        });
    },

    applyTheme(theme = defaultTheme) {
      const generatedColors: IThemeColors = Object.keys(
        defaultTheme.colors
      ).reduce((acc, key) => {
        acc[key as keyof IThemeColors] = generateShades(
          theme.colors[key as keyof IThemeColors]
        );
        return acc;
      }, {} as IThemeColors);
      this.theme = { colors: generatedColors };
      this.files.theme = this.theme;

      Object.entries(this.theme.colors).forEach(([key, shades]) => {
        Object.entries(shades).forEach(([shade, value]) => {
          document.documentElement.style.setProperty(
            `--color-${key}-${shade}`,
            value
          );
        });
      });
    },
  },
});

export const useThemeStore = store(instance);
